import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';

import {
	EmployeeChangesFilterOptionsSchema,
	EmployeesChanges,
} from '@/constants/EmployeesChangesConstants';
import { filters } from '@/constants/filters/EmployeeChangesFilter';
import api from '@/services/apiSgft';
import { EmployeeChangesFiltersAtom } from '@/state/EmployeeChanges.atom';

import EmptyIcon from '../../assets/icons/empty.svg';
import { columns } from '../../constants/tableColumns/EmployeesChangesColumns';
import NewSideFilter from '../molecules/NewSideFilter';
import ViewDataWrapper from '../molecules/ViewDataWrapper';
import InfiniteTable from './InfiniteTable';

const EmployeesChangesCard = () => {
	const filterValues = useRecoilValue(EmployeeChangesFiltersAtom);

	const fetchChanges = async (context: {
		pageParam?: number;
	}): Promise<{
		count: number;
		nextPage: number;
		notifications: EmployeesChanges[];
	}> => {
		try {
			const response = await api.get('/training-modification-notification', {
				params: {
					page: context.pageParam ?? 1,
					size: 10,
					status: filterValues.status,
					employee: filterValues.employee,
					training: filterValues.trainings,
					type: filterValues.types,
				},
			});
			return response.data;
		} catch (e) {
			toast.error('Erro ao carregar os dados', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		}
	};

	const {
		data,
		isLoading,
		isError,
		fetchNextPage,
		isFetchingNextPage,
		hasNextPage,
	} = useInfiniteQuery(['training-control', filterValues], fetchChanges, {
		retry: false,
		getNextPageParam: (actualPage) => {
			return actualPage.nextPage;
		},
	});

	const notifications = data?.pages
		? data.pages.flatMap((page) => page.notifications || [])
		: [];

	const fetchFilterOptions = async () => {
		try {
			const response = await api.get(
				'training-modification-notification/filter',
				{
					params: {
						status: filterValues.status,
						employee: filterValues.employee,
						training: filterValues.trainings,
						type: filterValues.types,
					},
				},
			);
			const parsedData = EmployeeChangesFilterOptionsSchema.parse(
				response.data,
			);
			return parsedData;
		} catch (error) {
			toast.error('Erro ao carregar os dados', {
				theme: 'colored',
				toastId: 'error',
			});
			throw error;
		}
	};

	const { data: filterOptions, isLoading: isLoadingFilter } = useQuery(
		['training-modification-filters', filterValues],
		fetchFilterOptions,
	);

	return (
		<>
			<NewSideFilter
				filters={filters}
				atom={EmployeeChangesFiltersAtom}
				options={filterOptions}
				isLoading={isLoadingFilter}
			/>
			<ViewDataWrapper
				isError={isError}
				isLoading={isLoading}
				className="max-w-[100%]"
			>
				{notifications.length ? (
					<div className="flex min-w-full flex-col rounded-md bg-white">
						<InfiniteTable
							hasNextPage={hasNextPage}
							data={notifications}
							columns={columns}
							fetchNextPage={fetchNextPage}
							isFetchingNextPage={isFetchingNextPage}
							alignTextLeft
						/>
					</div>
				) : (
					<div className="flex h-[50vh] w-full flex-col items-center justify-center rounded-md bg-white">
						<img src={EmptyIcon} className="w-40" />
						<div className="w-full text-center text-sm font-semibold text-blue-800">
							<p>Nenhuma alteração encontrada</p>
						</div>
					</div>
				)}
			</ViewDataWrapper>
		</>
	);
};
export default EmployeesChangesCard;
